import { useQuery, useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import type { Prismic } from '@landsbankinn/prismic';
import {
  Column,
  Row,
  theme,
  device,
  useMedia,
  Segment,
  typography,
  TextInput,
  EmailInput,
  Input,
  Checkbox,
  Button,
} from '@landsbankinn/ui';
import { LandsbankinnContext } from '@landsbankinn/utils';
import React, { useContext, useState } from 'react';
import type { SubscribeToEmailListFields } from '../../../__generated__/globalTypes';
import type { getMailLists } from '../../graphql/__generated__/getMailLists';
import { getMailLists as query } from '../../graphql/fetchEmailLists';
import type {
  subscribeToEmailList,
  subscribeToEmailListVariables,
} from '../../graphql/MailingList/__generated__/subscribeToEmailList';
import { subscribeToEmailList as muchacho } from '../../graphql/MailingList/subscribe';

interface IProps {
  categories: string;
  lists: ILists[];
}
interface ILists {
  category: string;
  title: string;
  text: string;
  order: number;
}

interface IField {
  id: Prismic.RichText;
  value: Prismic.RichText;
}

const Heading = styled.h2`
  ${typography.h2Big}

  margin-bottom: 20px;
`;
const FormInputContainer = styled.div`
  margin-bottom: 30px;
`;

const FormCheckbox = styled(Checkbox)`
  margin-bottom: 15px;

  label {
    color: rgb(30, 61, 90);
    ${typography.h4}
    padding-left: 50px;
    height: auto;
    background: transparent;
  }
  label:before {
    border: 1px solid rgb(30, 60, 90);
  }
`;

const CategoryHeader = styled.div<{ index: number }>`
  color: rgb(30, 61, 90);
  ${typography.h3}
  font-weight: 500;
  padding-bottom: 25px;
  width: 33.3333%;
  border-bottom: 4px solid
    ${(props) =>
      props.index === 0
        ? `rgb(32, 68, 106)`
        : props.index === 1
        ? `rgb(255, 175, 112)`
        : `rgb(114, 220, 235)`};
  margin-right: 10px;
`;
const CategoryContainer = styled.div`
  margin-bottom: 74px;
  display: flex;
`;
const CategoryNoText = styled.div`
  border-bottom: 4px solid rgb(232, 235, 238);
  width: 100%;
`;

const ItemText = styled.div`
  color: rgb(22, 62, 99);

  margin-left: 52px;
  margin-bottom: 72px;
`;
const BackgroundBox = styled.div`
  background: rgb(244, 247, 247);
  padding: 44px 0px;

  @media ${device.tablet} {
    padding: 72px 100px;
  }
  @media ${device.desktop} {
    padding: 72px 100px;
  }
`;

const P = styled.p`
  ${typography.p2}
  margin-bottom: 30px;
  @media ${device.desktop} {
    /* margin-bottom: 150px; */
  }
`;

const FormRow = styled(Row)`
  border-top: 4px solid rgba(30, 61, 90, 0.1);
  padding-top: 72px;
  > div {
    padding: 0;
  }
  > div:last-child {
    margin-top: 30px;
  }
  @media ${device.desktop} {
    > div:first-child {
      padding-left: 0px;
    }
    > div:last-child {
      margin-top: 0;
      padding-right: 0px;
    }
  }
`;
const FormHeader = styled.div`
  color: rgb(22, 62, 99);
  ${typography.h2}
  line-height: 56px;
  margin-bottom: 40px;
`;
const FormText = styled.div``;
const FormButton = styled(Button)`
  width: 100%;
`;

const A = styled.a`
  text-decoration: underline;
`;

const FormContainer: React.FC = ({ children }) => {
  const isMobile = useMedia('sm');

  if (isMobile) {
    return (
      <BackgroundBox>
        <Segment>{children}</Segment>
      </BackgroundBox>
    );
  }

  return (
    <Segment>
      <BackgroundBox>{children}</BackgroundBox>
    </Segment>
  );
};

export function MailingList({ categories, lists }: IProps) {
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [checked, setChecked] = useState<string[]>([]);
  const { client } = useContext(LandsbankinnContext);
  const [fields, setFields] = useState<SubscribeToEmailListFields[]>([]);
  const [onFormSubmit] = useMutation<
    subscribeToEmailList,
    subscribeToEmailListVariables
  >(muchacho, {
    client,
    variables: {
      data: {
        fields: fields,
        mailinglists: checked,
        email,
      },
    },
    onCompleted: (data) => {
      setSubmitted(data.subscribeToEmailList.worked);
    },
  });

  const { data, loading } = useQuery<getMailLists>(query, {
    client,
    onCompleted: (data) => {
      setFields(
        data?.getEmailLists.fields.map((x) => {
          return {
            key: x.key,
            value: x.value,
          };
        }),
      );
    },
  });

  return (
    <>
      <Segment>
        <Row>
          <Column sm={{ width: 12 / 12 }} md={{ width: 6 / 12 }}>
            <Heading>Póstlistar</Heading>
          </Column>
          <Column sm={{ width: 12 / 12 }} md={{ width: 6 / 12 }}>
            <P>
              Fáðu reglulegar tilkynningar um nýtt útgáfuefni Landsbankans með
              því að skrá þig á einn eða fleiri þeirra póstlista sem finna má á
              þessari síðu.
            </P>
          </Column>
        </Row>
      </Segment>
      <FormContainer>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onFormSubmit();
          }}
        >
          {loading && <p>Hleð inn póstlistum...</p>}
          {!loading &&
            categories.split(',').map((category, index) =>
              lists
                .filter((x) => x.category === category)
                .sort((y) => y.order)
                .map((listItem) => {
                  const key = data?.getEmailLists.mailingLists.find(
                    (x) => x.value === listItem.title,
                  )?.key;

                  return (
                    <div key={listItem.title}>
                      {listItem.order === 1 && (
                        <CategoryContainer>
                          <CategoryHeader index={index}>
                            {listItem.category}
                          </CategoryHeader>
                          <CategoryNoText />
                        </CategoryContainer>
                      )}
                      <div>
                        <FormCheckbox
                          checked={checked.includes(key as any)}
                          label={listItem.title}
                          id={listItem.title}
                          onChange={(value) => {
                            if (value) {
                              setChecked((c) => [...c, key as any]);
                            } else {
                              setChecked((c) => [
                                ...c.filter((b) => b !== key),
                              ]);
                            }
                          }}
                          variant="noBorder"
                        />
                        <ItemText>{listItem.text}</ItemText>
                      </div>
                    </div>
                  );
                }),
            )}

          <FormRow>
            {submitted && (
              <Column sm={12 / 12} lg={7 / 12} width={7 / 12}>
                <FormHeader>Takk fyrir skráninguna!</FormHeader>
              </Column>
            )}
            {!submitted && (
              <Column sm={12 / 12} lg={6 / 12} width={6 / 12}>
                <FormHeader>Skráðu þig á póstlista</FormHeader>
                <FormText>
                  Með því að skrá mig á póstlista samþykki ég{' '}
                  <A href="/bankinn/um-bankann/personuvernd">
                    persónuverndarstefnu
                  </A>{' '}
                  bankans.
                </FormText>
              </Column>
            )}
            {!submitted && (
              <Column sm={12 / 12} lg={6 / 12} width={6 / 12}>
                <FormInputContainer>
                  <Input
                    label="Nafn"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    labelVariant="aboveInput"
                  />
                </FormInputContainer>
                <FormInputContainer>
                  <EmailInput
                    label="Netfang"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    labelVariant="aboveInput"
                  />
                </FormInputContainer>
                <FormButton
                  bg={theme.colors.primary}
                  type="submit"
                  title="Skrá mig"
                />
              </Column>
            )}
          </FormRow>
        </form>
      </FormContainer>
    </>
  );
}
